import { useEffect, useLayoutEffect, useMemo } from "react";
import { View } from "react-native";
import { CommonActions } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useLocale } from "@src/packages/i18n";
import type { ChatStackParamList } from "../../types";
import { ConditionDetailContent } from "../../components/ConditionDetailSheet";
import { getFactSheetRouteBlock } from "../../components/factSheetRouteIds";
import type { FactSheetLinkTarget } from "../../components/LinkedFactSheetText";
import { useSmartBack } from "../../hooks/useSmartBack";

type Props = NativeStackScreenProps<ChatStackParamList, "ConditionDetail">;

export default function ConditionDetailScreen({ route, navigation }: Props) {
  const { conditionId, returnTab } = route.params;
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Learn",
    fallbackScreen: "Conditions",
  });

  const condition = useMemo(() => {
    void locale;
    return getFactSheetRouteBlock("condition", conditionId);
  }, [conditionId, locale]);

  useEffect(() => {
    if (!condition) {
      navigation.goBack();
    }
  }, [condition, navigation]);

  useLayoutEffect(() => {
    if (condition) {
      navigation.setOptions({ title: condition.title });
    }
  }, [condition, navigation]);

  if (!condition) return null;

  const openFactSheetLink = (target: FactSheetLinkTarget) => {
    switch (target.kind) {
      case "condition":
        navigation.navigate("ConditionDetail", { conditionId: target.id });
        return;
      case "medication":
        navigation.navigate("MedicationDetail", { medicationId: target.id });
        return;
      case "procedure":
        navigation.navigate("ProcedureDetail", { procedureId: target.id });
        return;
      case "test":
        navigation.navigate("TestDetail", { testId: target.id });
        return;
      case "supplement":
        navigation.getParent()?.dispatch(
          CommonActions.navigate({
            name: "Access",
            params: {
              screen: "SupplementDetail",
              params: { supplementId: target.id },
            },
          }),
        );
        return;
      case "therapy":
        navigation.getParent()?.dispatch(
          CommonActions.navigate({
            name: "Access",
            params: {
              screen: "TherapyDetail",
              params: { therapyId: target.id },
            },
          }),
        );
        return;
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <ConditionDetailContent
        item={condition}
        onClose={handleBack}
        onOpenFactSheet={openFactSheetLink}
        skipExitAnimation
      />
    </View>
  );
}
